/**
 * Credenciales que inyecta el host (RN o checkout web) en `window.__CARREFOUR_PAYMENT_INIT__`.
 * - App: modo **direct** (Bearer + sessionToken + baseUrl NAT).
 * - Web: modo **proxy** (checkout-proxy + checkoutSessionId).
 */
import type { PaymentCredentials } from '../infrastructure/paymentMiddlewareClient';
import { isProxyPaymentCredentials } from '../infrastructure/paymentMiddlewareClient';

export const PAYMENT_INIT_GLOBAL = '__CARREFOUR_PAYMENT_INIT__';

export interface PaymentInit extends PaymentCredentials {
  /** Total del pedido en pesos: respaldo si `pre-initiate` falla. */
  totalAmount?: number;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const CHECKOUT_SESSION_ID_PATTERN = /^[a-f0-9]{64}$/i;

export const parsePaymentInit = (raw: unknown): PaymentInit | undefined => {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  const { totalAmount, mode, proxyBaseUrl, checkoutSessionId, baseUrl, bearerToken, sessionToken } =
    record;

  const validTotal =
    typeof totalAmount === 'number' && Number.isFinite(totalAmount) && totalAmount > 0;
  const totalPart = validTotal ? { totalAmount } : {};

  const wantsProxy =
    mode === 'proxy' ||
    (isNonEmptyString(proxyBaseUrl) && isNonEmptyString(checkoutSessionId));

  if (wantsProxy) {
    if (
      !isNonEmptyString(proxyBaseUrl) ||
      !isNonEmptyString(checkoutSessionId) ||
      !CHECKOUT_SESSION_ID_PATTERN.test(checkoutSessionId)
    ) {
      return undefined;
    }
    return { mode: 'proxy', proxyBaseUrl, checkoutSessionId, ...totalPart };
  }

  if (!isNonEmptyString(baseUrl) || !isNonEmptyString(bearerToken) || !isNonEmptyString(sessionToken)) {
    return undefined;
  }

  return { mode: 'direct', baseUrl, bearerToken, sessionToken, ...totalPart };
};

export const readPaymentInit = (source: Window = window): PaymentInit | undefined =>
  parsePaymentInit((source as unknown as Record<string, unknown>)[PAYMENT_INIT_GLOBAL]);

/** Identidad de sesión de pago (sin total): evita repetir `GET payment/card` si el host reenvía `payment-init`. */
export const paymentInitSessionKey = (init: PaymentInit): string => {
  if (isProxyPaymentCredentials(init)) {
    return `proxy:${init.proxyBaseUrl}|${init.checkoutSessionId}`;
  }
  return `direct:${init.baseUrl}|${init.sessionToken}`;
};

export const HOST_CHECKOUT_SOURCE = 'carrefour-checkout-web';
export const HOST_PAYMENT_INIT_MESSAGE = 'payment-init';
/** El iframe embebido lo envía si el `payment-init` del host llegó antes del listener (race en onLoad). */
export const HOST_REQUEST_PAYMENT_INIT_MESSAGE = 'request-payment-init';

export const applyPaymentInit = (raw: unknown): PaymentInit | undefined => {
  const parsed = parsePaymentInit(raw);
  if (!parsed) return undefined;
  (window as unknown as Record<string, unknown>)[PAYMENT_INIT_GLOBAL] = parsed;
  return parsed;
};

const defaultAllowedOrigins = (): string[] => {
  const origins = new Set<string>();
  if (typeof window !== 'undefined') origins.add(window.location.origin);
  [
    'https://checkout-ecomnattesting.carrefour.com.ar',
    'https://checkout-poc-ecomnattesting.carrefour.com.ar',
    'http://127.0.0.1:5175',
    'http://localhost:5175',
    'http://127.0.0.1:5173',
    'http://localhost:5173',
  ].forEach((origin) => origins.add(origin));
  return [...origins];
};

/** Checkout web (cross-origin): el host envía credenciales por postMessage tras cargar el iframe. */
export const listenForHostPaymentInit = (
  onInit: (init: PaymentInit) => void,
  allowedOrigins: string[] = defaultAllowedOrigins(),
): (() => void) => {
  const handler = (event: MessageEvent) => {
    if (!allowedOrigins.includes(event.origin)) return;
    const data = event.data as { source?: string; type?: string; credentials?: unknown } | null;
    if (!data || data.source !== HOST_CHECKOUT_SOURCE || data.type !== HOST_PAYMENT_INIT_MESSAGE) {
      return;
    }
    const init = applyPaymentInit(data.credentials);
    if (init) onInit(init);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
};

export { isProxyPaymentCredentials };
